import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import type { Expression } from 'mapbox-gl';
import type { FeatureCollection } from 'geojson';

import { circlePaint, mapColors } from 'components/map';

import type { MapQueryListedShapes, MapQueryNonTractFeatures } from './types';
import { FOCUS_COLOR, NON_TRACT_ROW_ID, tractPaint } from './utils';

export const LISTED_SOURCE_ID = 'package-map-query-listed-tracts';
export const LISTED_FILL_LAYER = `${LISTED_SOURCE_ID}-fill`;
export const LISTED_LINE_LAYER = `${LISTED_SOURCE_ID}-line`;
export const LISTED_FOCUS_LAYER = `${LISTED_SOURCE_ID}-focus`;

export const NON_TRACT_SOURCE_ID = 'package-map-query-non-tracts';
/** An LTD drawn by its unit's shape. */
export const NON_TRACT_FILL_LAYER = `${NON_TRACT_SOURCE_ID}-fill`;
export const NON_TRACT_OUTLINE_LAYER = `${NON_TRACT_SOURCE_ID}-outline`;
/** A well path or a planned lateral. */
export const NON_TRACT_LINE_LAYER = `${NON_TRACT_SOURCE_ID}-line`;
/** A well with only its surface hole. */
export const NON_TRACT_DOT_LAYER = `${NON_TRACT_SOURCE_ID}-dot`;
/** Invisible and wider than a line: what a click on a line can focus. */
export const NON_TRACT_HIT_LAYER = `${NON_TRACT_SOURCE_ID}-hit`;
export const NON_TRACT_FOCUS_LAYER = `${NON_TRACT_SOURCE_ID}-focus`;

const NO_FEATURES: FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};
// Mapbox's `==` needs a string to compare with; no row id is this.
const NO_FOCUS = '__nofocus__';
const POLYGONS: Expression = ['==', ['geometry-type'], 'Polygon'];
const LINES: Expression = ['==', ['geometry-type'], 'LineString'];
const POINTS: Expression = ['==', ['geometry-type'], 'Point'];

export interface AssetLayersProps {
  /** The listed Tracts' newest shapes; null with none listed. */
  listed: MapQueryListedShapes | null;
  /** Every matching wellbore and LTD asset's shape; undefined before the search answers. */
  nonTracts: MapQueryNonTractFeatures | undefined;
  /** Ticked entities, in tick order (the order their colours follow). */
  entityIds: number[];
  /** The focused row's id (`resultRowId`). */
  focusedId: string | null;
  /** The tracts source's minzoom, below which outlines thin as the tract layer's do. */
  tractMinZoom: number;
}

/**
 * The results the tract tiles do not draw: the Tracts a list adds, which are
 * results whatever the filters, and the wellbore and LTD assets. A listed Tract,
 * and an LTD drawn by its unit's shape, is painted as the tract layer paints a
 * Tract: its first ticked owner's colour, outlined in the result blue. A well is
 * drawn as the app draws wells (components/map/wells-layer.tsx): its surface hole
 * a red dot that grows as the map zooms in, its path, as an LTD's planned
 * lateral, a thin stick line. The focused one is orange. Mounted with the tract
 * layer and then always, even with nothing to draw, so its layers sit over the
 * tract layers, under the polygon drawing. Clicks are the tract layer's, which
 * listens on the fill, hit and dot layers here too, so the topmost result drawn
 * answers.
 *
 * @param {AssetLayersProps} props - The shapes, the ticks and the focus
 * @return {JSX.Element} The two sources and their layers
 */
export function AssetLayers({
  listed,
  nonTracts,
  entityIds,
  focusedId,
  tractMinZoom,
}: AssetLayersProps) {
  const paint = useMemo(
    () => tractPaint(entityIds, tractMinZoom),
    [entityIds, tractMinZoom]
  );
  const focus = focusedId ?? NO_FOCUS;
  return (
    <>
      <Source id={LISTED_SOURCE_ID} type="geojson" data={listed ?? NO_FEATURES}>
        <Layer
          id={LISTED_FILL_LAYER}
          type="fill"
          paint={{
            'fill-color': paint.fillColor,
            'fill-opacity': paint.fillOpacity,
          }}
        />
        <Layer
          id={LISTED_LINE_LAYER}
          type="line"
          paint={{
            'line-color': paint.lineColor,
            'line-width': paint.lineWidth,
          }}
        />
        <Layer
          id={LISTED_FOCUS_LAYER}
          type="line"
          filter={['==', ['get', 'tract_id'], focus]}
          paint={{ 'line-color': FOCUS_COLOR, 'line-width': 3 }}
        />
      </Source>
      <Source
        id={NON_TRACT_SOURCE_ID}
        type="geojson"
        data={nonTracts ?? NO_FEATURES}
      >
        <Layer
          id={NON_TRACT_FILL_LAYER}
          type="fill"
          filter={POLYGONS}
          paint={{
            'fill-color': paint.fillColor,
            'fill-opacity': paint.fillOpacity,
          }}
        />
        <Layer
          id={NON_TRACT_OUTLINE_LAYER}
          type="line"
          filter={POLYGONS}
          paint={{
            'line-color': paint.lineColor,
            'line-width': paint.lineWidth,
          }}
        />
        <Layer
          id={NON_TRACT_LINE_LAYER}
          type="line"
          filter={LINES}
          paint={{ 'line-color': mapColors.stickFill, 'line-width': 0.5 }}
        />
        <Layer
          id={NON_TRACT_HIT_LAYER}
          type="line"
          filter={LINES}
          paint={{ 'line-opacity': 0, 'line-width': 12 }}
        />
        <Layer
          id={NON_TRACT_DOT_LAYER}
          type="circle"
          filter={POINTS}
          paint={{
            ...circlePaint(mapColors.surfaceHoleFill),
            'circle-stroke-width': [
              'case',
              ['==', NON_TRACT_ROW_ID, focus],
              2,
              0,
            ],
            'circle-stroke-color': FOCUS_COLOR,
          }}
        />
        <Layer
          id={NON_TRACT_FOCUS_LAYER}
          type="line"
          filter={['==', NON_TRACT_ROW_ID, focus]}
          paint={{ 'line-color': FOCUS_COLOR, 'line-width': 3 }}
        />
      </Source>
    </>
  );
}
